import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Modal } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { BankSelectionBody } from '../../../BankSelectionModal/BankSelectionBody';
import { useBankSelectionFlow } from '../../../BankSelectionModal/useBankSelectionFlow';
import { ChooseNewBankAccountVerification } from '../chooseNewBankAccountVerification/ChooseNewBankAccountVerification';
import { UploadBankStatementsForVerification } from '../uploadBankStatementsForVerification/UploadBankStatementsForVerification';

type TProps = {
    onClose: () => void;
    isModalOpen: boolean;
    isBankEvidence?: boolean;
};
export type TNewBankAccountModalName =
    null | 'open_banking' | 'manual_bs_upload';

export const VerifyNewBankAccountModal = ({
    onClose,
    isModalOpen,
    isBankEvidence = false
}: TProps) => {
    const [currentModal, setCurrentModal] =
        useState<TNewBankAccountModalName>(null);

    const updateModalStep = (step: TNewBankAccountModalName) => {
        setCurrentModal(step);
    };

    useEffect(() => {
        if (currentModal === 'open_banking') {
            tracking.ampli.addBankDetailsChooseLinkOpenBanking();
        } else if (currentModal === 'manual_bs_upload') {
            tracking.ampli.addBankDetailsChooseManualBsUpload();
        }
    }, [currentModal]);

    return (
        <Modal
            headerBar={true}
            onClose={() => {
                updateModalStep(null);
                onClose();
            }}
            isOpen={isModalOpen}
            closeIcon={true}
            data-testid="verify-new-bank-account-modal"
            title={
                <ModalTitle
                    modalName={currentModal}
                    isBankEvidence={isBankEvidence}
                />
            }
        >
            <ModalBody
                modalName={currentModal}
                onClose={() => {
                    onClose();
                }}
                updateModalStep={updateModalStep}
                isBankEvidence={isBankEvidence}
            />
        </Modal>
    );
};

const ModalTitle = ({
    modalName,
    isBankEvidence
}: {
    modalName: TNewBankAccountModalName;
    isBankEvidence: boolean;
}) => {
    switch (modalName) {
        case 'manual_bs_upload':
            return (
                <FormattedMessage
                    id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.title"
                    defaultMessage="Upload a statement"
                />
            );
        case 'open_banking':
            return isBankEvidence ? (
                <FormattedMessage
                    id="_.funding_requirements.bank_evidence.connect_business_account_title"
                    defaultMessage="Connect your business account"
                />
            ) : (
                <FormattedMessage
                    id="_.bank_selection_modal.verifiction_title"
                    defaultMessage="Link your business account"
                />
            );
        default:
            return (
                <FormattedMessage
                    id="_.funding_requirements.add_bank_details.choose_verification_method_title"
                    defaultMessage="How would you like to verify your account?"
                />
            );
    }
};

const ModalBody = ({
    modalName,
    updateModalStep,
    onClose,
    isBankEvidence
}: {
    modalName: TNewBankAccountModalName;
    onClose: () => void;
    updateModalStep: (nextStep: TNewBankAccountModalName) => void;
    isBankEvidence: boolean;
}) => {
    switch (modalName) {
        case 'manual_bs_upload':
            return (
                <UploadBankStatementsForVerification
                    changeVerificationMethod={updateModalStep}
                    onClose={onClose}
                    isBankEvidence={isBankEvidence}
                />
            );
        case 'open_banking':
            return (
                <OpenBankingBody
                    isBankEvidence={isBankEvidence}
                    onClose={onClose}
                />
            );
        default:
            return (
                <ChooseNewBankAccountVerification
                    changeVerificationMethod={updateModalStep}
                    isBankEvidence={isBankEvidence}
                />
            );
    }
};

const OpenBankingBody = ({
    isBankEvidence,
    onClose
}: {
    isBankEvidence: boolean;
    onClose: () => void;
}) => {
    const flow = useBankSelectionFlow('choose');

    return (
        <BankSelectionBody
            // Bank verification is DE-only; UK bank evidence uses the
            // standard open-banking endpoint.
            linkingForBankVerification={!isBankEvidence}
            isBankEvidence={isBankEvidence}
            step={flow.step}
            selectedBank={flow.selectedBank}
            selectBank={flow.selectBank}
            unselectBank={flow.unselectBank}
            onLinkAnother={flow.linkAnother}
            onDone={onClose}
        />
    );
};
